/**
 * Consumer side of {@link purchasedItineraryOpenSlice}: resolves the persisted
 * purchased entry and hands its itinerary to the caller once per request
 * revision — the same selection the saved list performs on a manual click.
 */

import { useCallback, useEffect, useRef } from "react";
import { purchasedItineraryOpenSlice, useEphemeralSelector } from "../../../store/ephemeral";
import { purchasedItinerariesSlice, usePersistedSliceSelector, type PurchasedItinerariesState } from "../../../store/persistence";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";

/**
 * Reacts to purchased-itinerary open requests emitted after a successful purchase.
 *
 * @param onOpenItinerary Callback receiving the purchased entry itinerary (typically
 * the same handler as a saved-list click, after switching the list to saved mode).
 *
 * @remarks The request is applied only once the persisted entry is actually
 * readable; the revision stays unapplied while the lookup misses so a late
 * store propagation still opens the entry instead of swallowing the request.
 */
export const usePurchasedItineraryOpen = ( onOpenItinerary: ( itinerary: ItineraryDTO ) => void ): void => {
    const request = useEphemeralSelector( purchasedItineraryOpenSlice, ( state ) => state );
    const itineraryId = request.itineraryId;
    const selectPurchasedItinerary = useCallback(
        ( entries: PurchasedItinerariesState ): ItineraryDTO | null => {
            if ( itineraryId === null ) {
                return null;
            }

            return entries.find( ( entry ) => entry.itinerary.id === itineraryId )?.itinerary ?? null;
        },
        [ itineraryId ],
    );
    const purchasedItinerary = usePersistedSliceSelector( purchasedItinerariesSlice, selectPurchasedItinerary );

    /** Last applied request revision; starts at the mount value so an older request is never replayed. */
    const appliedRevisionRef = useRef( request.revision );

    useEffect( () => {
        if ( request.revision === appliedRevisionRef.current || purchasedItinerary === null ) {
            return;
        }

        appliedRevisionRef.current = request.revision;
        onOpenItinerary( purchasedItinerary );
    }, [ onOpenItinerary, purchasedItinerary, request.revision ] );
};
